import {
  Box,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import Hint from 'ui/shared/Hint';
import IconSvg from 'ui/shared/IconSvg';

interface Stats {
  activeProviders: number;
  activeClients: number;
  totalTasks: number;
  successRate: number;
  formattedDailyTasksData?: Array<{
    name: string;
    tasks: number;
  }>;
}

interface StatsData {
  status: string;
  message: string;
  data: Stats;
  formattedDailyTasksData?: Array<{
    name: string;
    tasks: number;
  }>;
}

// Function to format date for the chart
function formatDateTick(value: string) {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

const ProviderStats = () => {
  const [ stats, setStats ] = useState<Stats>({
    activeProviders: 0,
    activeClients: 0,
    totalTasks: 0,
    successRate: 0,
    formattedDailyTasksData: [],
  });

  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const hint = 'Tasks processed in the network over the last 30 days';

  // Fetch stats data function
  const fetchStatsData = () => {
    fetch('https://management-backend.opengpu.network/api/dashboard/stats')
      .then((res) => res.json())
      .then((rawData: unknown) => {
        const data = rawData as StatsData;
        setStats({
          activeProviders: data?.data.activeProviders || 0,
          activeClients: data?.data.activeClients || 0,
          totalTasks: data?.data.totalTasks || 0,
          successRate: data?.data.successRate || 0,
          formattedDailyTasksData:
            data?.data.formattedDailyTasksData || generateMockChartData(),
        });
      })
      .catch(() => {
        // On error, use mock data
        setStats({
          activeProviders: 0,
          activeClients: 0,
          totalTasks: 0,
          successRate: 0,
          formattedDailyTasksData: generateMockChartData(),
        });
      });
  };

  useEffect(() => {
    // Initial fetch
    fetchStatsData();

    // Set up interval to fetch data every 5 seconds
    const intervalId = setInterval(fetchStatsData, 43_000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const tasksData =
    stats.formattedDailyTasksData && stats.formattedDailyTasksData.length > 0 ?
      stats.formattedDailyTasksData.slice(
        0,
        stats.formattedDailyTasksData.length - 1,
      ) :
      stats.formattedDailyTasksData || [];

  return (
    <Box>
      { /* Stats Cards - Now below the chart in a single row */ }
      <Grid
        templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
        gap={ 4 }
      >
        { /* Total Providers */ }
        <GridItem>
          <Flex
            alignItems="center"
            bgColor={ cardBg }
            p={ 3 }
            borderRadius="base"
            justifyContent="space-between"
            columnGap={ 2 }
          >
            <IconSvg
              name="networks"
              p={ 2 }
              boxSize="36px"
              borderRadius="base"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={ 0 }
            />
            <Box w={{ base: '100%', lg: 'calc(100% - 44px)' }} minH="40px">
              <Text
                color="text_secondary"
                fontSize="xs"
                lineHeight="16px"
                fontWeight="normal"
                whiteSpace="nowrap"
              >
                Total Providers
              </Text>
              <Text
                display="flex"
                alignItems="baseline"
                fontWeight={ 500 }
                fontSize="lg"
                lineHeight={ 6 }
              >
                { formatNumber(stats.activeProviders) }
              </Text>
            </Box>
          </Flex>
        </GridItem>

        { /* Total Clients */ }
        <GridItem>
          <Flex
            alignItems="center"
            bgColor={ cardBg }
            p={ 3 }
            borderRadius="base"
            justifyContent="space-between"
            columnGap={ 2 }
          >
            <IconSvg
              name="profile"
              p={ 2 }
              boxSize="36px"
              borderRadius="base"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={ 0 }
            />
            <Box w={{ base: '100%', lg: 'calc(100% - 44px)' }} minH="40px">
              <Text
                color="text_secondary"
                fontSize="xs"
                lineHeight="16px"
                fontWeight="normal"
                whiteSpace="nowrap"
              >
                Total Clients
              </Text>
              <Text
                display="flex"
                alignItems="baseline"
                fontWeight={ 500 }
                fontSize="lg"
                lineHeight={ 6 }
              >
                { formatNumber(stats.activeClients) }
              </Text>
            </Box>
          </Flex>
        </GridItem>

        { /* Total Tasks */ }
        <GridItem>
          <Flex
            alignItems="center"
            bgColor={ cardBg }
            p={ 3 }
            borderRadius="base"
            justifyContent="space-between"
            columnGap={ 2 }
          >
            <IconSvg
              name="transactions_slim"
              p={ 2 }
              boxSize="36px"
              borderRadius="base"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={ 0 }
            />
            <Box w={{ base: '100%', lg: 'calc(100% - 44px)' }} minH="40px">
              <Text
                color="text_secondary"
                fontSize="xs"
                lineHeight="16px"
                fontWeight="normal"
                whiteSpace="nowrap"
              >
                Total Tasks
              </Text>
              <Text
                display="flex"
                alignItems="baseline"
                fontWeight={ 500 }
                fontSize="lg"
                lineHeight={ 6 }
              >
                { formatNumber(stats.totalTasks) }
              </Text>
            </Box>
          </Flex>
        </GridItem>

        { /* Completion Rate */ }
        <GridItem>
          <Flex
            alignItems="center"
            bgColor={ cardBg }
            p={ 3 }
            borderRadius="base"
            justifyContent="space-between"
            columnGap={ 2 }
          >
            <IconSvg
              name="status/success"
              p={ 2 }
              boxSize="36px"
              borderRadius="base"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={ 0 }
            />
            <Box w={{ base: '100%', lg: 'calc(100% - 44px)' }} minH="40px">
              <Text
                color="text_secondary"
                fontSize="xs"
                lineHeight="16px"
                fontWeight="normal"
                whiteSpace="nowrap"
              >
                Task Completion
              </Text>
              <Text
                display="flex"
                alignItems="baseline"
                fontWeight={ 500 }
                fontSize="lg"
                lineHeight={ 6 }
              >
                { stats.successRate.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) }
                %
              </Text>
            </Box>
          </Flex>
        </GridItem>
      </Grid>

      { /* Task Volume Chart - Now at the top */ }
      <Card bg={ cardBg } boxShadow="sm" mt={ 4 }>
        <CardBody>
          <Flex alignItems="center" mb={ 2 }>
            <Text fontWeight="medium">Task Volume by Date</Text>
            { hint && <Hint label={ hint } ml={ 1 }/> }
          </Flex>
          <Box h="150px" w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={ tasksData }
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3"/>
                <XAxis dataKey="name" tickFormatter={ formatDateTick }/>
                <YAxis/>
                <Tooltip/>
                <Legend/>
                <Line
                  type="monotone"
                  dataKey="tasks"
                  name="Number of Tasks"
                  stroke="#0088FE"
                  strokeWidth={ 2 }
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
};

// Function to generate mock chart data
function generateMockChartData() {
  const data = [];
  const today = new Date();

  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i);

    data.push({
      name: date.toISOString().split('T')[0],
      tasks: 0, // Random tasks between 500-1500
    });
  }

  return data;
}

export default ProviderStats;
